import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Inbox } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { humanizeApiError } from "../api/client";
import { type User, type UserCreatePayload, usersApi } from "../api/users";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Pagination } from "../components/ui/pagination";
import { Select } from "../components/ui/select";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

type RoleFilter = "" | "admin" | "manager";
type ActiveFilter = "" | "active" | "inactive";

export function UsersPage() {
  const [items, setItems] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("");
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [search]);

  const query = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      search: debouncedSearch || undefined,
      role: (roleFilter || undefined) as "admin" | "manager" | undefined,
      is_active:
        activeFilter === "active"
          ? true
          : activeFilter === "inactive"
            ? false
            : undefined,
    }),
    [page, debouncedSearch, roleFilter, activeFilter],
  );

  // Сбрасываем страницу на 1 при изменении фильтров.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, roleFilter, activeFilter]);

  const reload = useCallback(() => {
    setError(null);
    return usersApi
      .list(query)
      .then((r) => {
        setItems(r.items);
        setTotal(r.total_count);
      })
      .catch((err) => setError(extractMessage(err)));
  }, [query]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const hasFilters =
    debouncedSearch.length > 0 || roleFilter !== "" || activeFilter !== "";

  function clearFilters() {
    setSearch("");
    setRoleFilter("");
    setActiveFilter("");
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Сотрудники</h1>
        <Button onClick={() => setShowCreate(true)}>Добавить сотрудника</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          placeholder="Поиск по email или ФИО"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          aria-label="Поиск по email или ФИО"
        />
        <Select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
          className="w-auto"
          aria-label="Фильтр по роли"
        >
          <option value="">Все роли</option>
          <option value="admin">Админ</option>
          <option value="manager">Менеджер</option>
        </Select>
        <Select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
          className="w-auto"
          aria-label="Фильтр по активности"
        >
          <option value="">Любая активность</option>
          <option value="active">Активные</option>
          <option value="inactive">Отключённые</option>
        </Select>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">ФИО</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Роль</th>
                <th className="px-4 py-2 font-medium">Статус</th>
                <th className="px-4 py-2 font-medium">Последний вход</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    {hasFilters ? (
                      <div className="space-y-2">
                        <p className="text-muted-foreground">
                          Ничего не нашли по вашему запросу
                        </p>
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="text-sm underline text-muted-foreground hover:no-underline"
                        >
                          Сбросить фильтры
                        </button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-2 font-mono text-sm text-muted-foreground">
                        <Inbox className="h-3.5 w-3.5" aria-hidden="true" />
                        Здесь пока ничего нет
                      </span>
                    )}
                  </td>
                </tr>
              )}
              {items.map((u) => (
                <tr key={u.id} className="border-b last:border-b-0 hover:bg-muted/50">
                  <td className="px-4 py-2">
                    <Link className="underline-offset-4 hover:underline" to={`/users/${u.id}`}>
                      {u.full_name}
                    </Link>
                  </td>
                  <td className="px-4 py-2">{u.email}</td>
                  <td className="px-4 py-2">{u.role === "admin" ? "Админ" : "Менеджер"}</td>
                  <td className="px-4 py-2">
                    {u.is_active ? (
                      <span className="inline-block rounded px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700">
                        Активен
                      </span>
                    ) : (
                      <span className="inline-block rounded px-2 py-0.5 text-xs bg-muted text-muted-foreground">
                        Отключён
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">{formatDate(u.last_login_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination
        page={page}
        totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        total={total}
        onPageChange={(p) => setPage(p)}
      />

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            setPage(1);
            void reload();
          }}
        />
      )}
    </div>
  );
}

interface CreateUserModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateUserModal({ onClose, onCreated }: CreateUserModalProps) {
  const [form, setForm] = useState<UserCreatePayload>({
    email: "",
    full_name: "",
    role: "manager",
    bitrix_user_id: "",
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    firstFieldRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, submitting]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const created = await usersApi.create({
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        role: form.role,
        bitrix_user_id: form.bitrix_user_id?.trim() || null,
      });
      onCreated();
      navigate(`/users/${created.id}`);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-user-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
      data-testid="create-user-modal"
    >
      <div className="w-full max-w-lg rounded-xl border bg-card text-card-foreground shadow-lg">
        <div className="p-6 space-y-1 border-b">
          <h2 id="create-user-title" className="text-lg font-semibold">
            Новый сотрудник
          </h2>
          <p className="text-sm text-muted-foreground">
            Письмо с приглашением не отправляется автоматически. Сообщите
            сотруднику адрес страницы входа — он сам запросит одноразовый код
            на этот email.
          </p>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">ФИО</Label>
              <Input
                id="full_name"
                ref={firstFieldRef}
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="role">Роль</Label>
              <Select
                id="role"
                value={form.role}
                onChange={(e) =>
                  setForm({ ...form, role: e.target.value as "admin" | "manager" })
                }
              >
                <option value="manager">Менеджер</option>
                <option value="admin">Админ</option>
              </Select>
              <p className="text-xs text-muted-foreground">
                Менеджер — создаёт свои документы и рассылки, видит только их.
                Админ — то же плюс управление сотрудниками, настройки Bitrix24,
                аудит и доступ ко всем рассылкам.
              </p>
            </div>
          </div>

          <div className="pt-2 border-t">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              aria-expanded={showAdvanced}
            >
              {showAdvanced ? "Скрыть дополнительно" : "Дополнительно"}
            </button>
            {showAdvanced && (
              <div className="mt-3 space-y-2">
                <Label htmlFor="bitrix_user_id">Bitrix user_id</Label>
                <Input
                  id="bitrix_user_id"
                  value={form.bitrix_user_id ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, bitrix_user_id: e.target.value })
                  }
                  placeholder="например, 42"
                />
                <p className="text-xs text-muted-foreground">
                  ID пользователя в Bitrix24 (целое число). Без него уведомления
                  о просмотре документа и истечении срока ссылки в Bitrix24 не
                  приходят. Можно заполнить позже.
                </p>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Отменить
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner /> Создать
                </span>
              ) : (
                "Создать"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("ru-RU");
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err, { entityLabel: "Сотрудник" });
}
